import { BTLocalStorage, BTSessionStorage, LocalStorageKeys } from "types/btStorage";

import { formattedPlural } from "utilities/string/string";

import {
    type IApiJobBuilderRequest,
    JobPickerFilterTypes,
    type JobPickerJob,
} from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import {
    type IJobPickerPersistantState,
    type IJobPickerSelectionMemory,
    JobIdTypes,
    JobPickerSelectModes,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import {
    getJobPickerStorageKey,
    getStatusTextForJobFilter,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";

import { JobStatus } from "entity/job/Job.api.types";

// TODO: highly recommanded that keyword searching be done on the serverside through the API
// API should be adjusted to support this, if it has not already, along with pagination instead of trying to handle this on the client
//
export function filterJobsList(
    jobsList: JobPickerJob[],
    filterText: string,
    templatesOnly: boolean,
    allJobText?: string,
): JobPickerJob[] {
    const filter = filterText.trim().toLocaleLowerCase();
    const filteredJobsList =
        filter === ""
            ? jobsList
            : jobsList.filter(
                  (x) =>
                      x.jobId === JobIdTypes.AllJobs ||
                      x.jobId === JobIdTypes.GlobalDocs ||
                      x.jobName.toLocaleLowerCase().includes(filter) ||
                      x.jobInfo?.ownerName?.toLocaleLowerCase()?.includes(filter),
              );

    // Check for special case with TimeClock with no jobs
    if (
        jobsList.length === 2 &&
        jobsList.filter((x) => !isSpecialJob(x.jobId, x.jobStatus)).length === 0
    ) {
        return jobsList.filter((x) => x.jobId !== JobIdTypes.AllJobs).map((x) => x);
    }

    // Set all listed count after omitting all listed, none, and special jobs
    const allListedJob = filteredJobsList.filter((x) => !isSpecialJob(x.jobId, x.jobStatus));
    const builderId = allListedJob.length > 0 ? allListedJob[0].builderId : undefined;
    setAllListedJobCount(
        allListedJob.length,
        filteredJobsList,
        templatesOnly,
        builderId,
        allJobText,
    );

    return filteredJobsList;
}

function setAllListedJobCount(
    count: number,
    jobsList: JobPickerJob[],
    templatesOnly: boolean,
    builderId?: number,
    allJobText?: string,
) {
    let statusText = "";
    try {
        const selectedJobStatuses: string[] = (
            JSON.parse(
                // eslint-disable-next-line no-restricted-globals
                localStorage.getItem(
                    getJobPickerStorageKey(
                        LocalStorageKeys.CurrentJobPickerFilter,
                        false,
                        builderId,
                    ),
                ) ?? "{}",
            )[JobPickerFilterTypes.Other] ?? ""
        ).split(",");
        if (selectedJobStatuses.length === 1 || selectedJobStatuses.length === 2) {
            statusText =
                selectedJobStatuses
                    .map((id) => getStatusTextForJobFilter(Number(id)))
                    .join(" and ") + " ";
        }
    } catch (e) {
        statusText = "";
    }
    const allListedJob = jobsList.find((x) => x.jobId === JobIdTypes.AllJobs);
    if (allListedJob !== undefined) {
        if (allJobText) {
            allListedJob.jobName = allJobText;
        } else {
            allListedJob.jobName = templatesOnly
                ? formattedPlural({
                      value: count,
                      one: "View 1 Template",
                      other: `All ${count} Listed Templates`,
                  })
                : formattedPlural({
                      value: count,
                      one: `View 1 ${statusText}Job`,
                      other: `All ${count} ${statusText}Jobs`,
                  });
        }
    }
}

export function isSpecialJob(jobId: number, jobStatus: JobStatus) {
    if (jobStatus === JobStatus.GeneralJob) {
        return true;
    }
    return [
        JobIdTypes.AllJobs,
        JobIdTypes.GlobalDocs,
        JobIdTypes.GlobalJob,
        JobIdTypes.NoJobs,
    ].includes(jobId);
}

export function getSelectedJobIdsForJobPicker(
    id: number,
    jobs: JobPickerJob[] | IApiJobBuilderRequest[],
    selectMode?: JobPickerSelectModes,
) {
    if (
        id === JobIdTypes.NoJobs ||
        (id === JobIdTypes.AllJobs && selectMode === JobPickerSelectModes.Single)
    ) {
        return null;
    }
    if (id === JobIdTypes.AllJobs) {
        return jobs
            .filter((x) => x.jobId !== JobIdTypes.AllJobs && x.jobId !== JobIdTypes.NoJobs)
            .map((x) => x.jobId);
    }
    return id;
}

export function getSelectedJobIds(): number[] {
    const jobPickerState = getJobPickerState();
    return jobPickerState.selectedJobIds.map((x) => Number(x));
}

export function getAllBuildersSelected(): boolean {
    const jobPickerState = getJobPickerState();
    return jobPickerState.allBuildersSelected ?? false;
}

function getModeKey(isTemplateMode: boolean): "jobs" | "templates" {
    return isTemplateMode ? "templates" : "jobs";
}

/**
 * Reads the stored jobsite selections, migrating the legacy shape that held a single selection
 * with no mode. Legacy values are treated as a jobs selection since template mode did not
 * persist a selection at the time they were written.
 */
function getSelectedJobsites(): {
    jobs?: IJobPickerSelectionMemory;
    templates?: IJobPickerSelectionMemory;
} {
    const storedValue = BTLocalStorage.get("bt-object-selectedJobsiteIdWithBuilder");
    if (storedValue === null || storedValue === undefined) {
        return {};
    }
    if ("jobId" in storedValue) {
        return { jobs: { jobId: storedValue.jobId, builderId: storedValue.builderId } };
    }
    return storedValue;
}

/**
 * Stores the selected jobsite for the given mode, so switching modes can restore it. Jobs and
 * templates are stored separately and neither overwrites the other.
 * @param jobId The jobsite ID to store
 * @param builderId The builder ID associated with the jobsite
 * @param isTemplateMode Which mode the jobsite was selected in
 */
export function setSelectedJobsiteIdWithBuilder(
    jobId: number,
    builderId: number,
    isTemplateMode: boolean,
): void {
    BTLocalStorage.set("bt-object-selectedJobsiteIdWithBuilder", {
        ...getSelectedJobsites(),
        [getModeKey(isTemplateMode)]: { jobId, builderId },
    });
}

/**
 * Gets the jobsite selected in the given mode, validating that the stored builder ID matches the
 * current builder
 * @param currentBuilderId The current builder ID to validate against
 * @param isTemplateMode Which mode to read the selection for, defaulting to the current mode
 * @returns The stored jobsite ID if the builder ID matches, null otherwise
 */
export function getSelectedJobsiteIdWithBuilder(
    currentBuilderId: number,
    isTemplateMode: boolean = getJobPickerState().isTemplateMode,
): number | null {
    const stored = getSelectedJobsites()[getModeKey(isTemplateMode)];

    if (stored !== undefined && stored.builderId === currentBuilderId) {
        return stored.jobId;
    }
    // No stored value for this mode, or it belongs to a different builder
    return null;
}

/**
 * Forgets the jobsite selected in the given mode, or in every mode when no mode is given. Used
 * when a stored selection is no longer valid, so it is not restored again.
 */
export function clearSelectedJobsiteIdWithBuilder(isTemplateMode?: boolean): void {
    if (isTemplateMode === undefined) {
        BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
        return;
    }
    BTLocalStorage.set("bt-object-selectedJobsiteIdWithBuilder", {
        ...getSelectedJobsites(),
        [getModeKey(isTemplateMode)]: undefined,
    });
}

export function getSelectedJobId(builderId: number): number {
    // Use the new helper function that validates builder ID
    const storedJobId = getSelectedJobsiteIdWithBuilder(builderId);
    if (storedJobId !== null && storedJobId !== undefined) {
        return storedJobId;
    }

    const jobPickerState = getJobPickerState();
    const selectedJobIds = jobPickerState.selectedJobIds;
    const isAllJobsSelected =
        jobPickerState.selectedJobIds.length > 1 && jobPickerState.isAllJobsSelected;
    // Fallback to session values if localstorage is not set.
    if (!selectedJobIds || selectedJobIds.length === 0) {
        const jobIds: string | number =
            // eslint-disable-next-line no-restricted-syntax
            (window as any).AsyncJobPickerHelper?.getSelectedJobIdList() || "";
        // If the job picker hasn't initialized, it will return an empty string
        if (jobIds === null || jobIds === "" || jobIds === JobIdTypes.NoJobs) {
            return JobIdTypes.NoJobs;
        }
        // If a single job is selected, it will return that jobId as a number
        else if (typeof jobIds === "number") {
            return jobIds;
        } else {
            // Otherwise, it'll be a comma separated list of jobs
            const jobList = jobIds.split(",").map((j) => Number(j));
            if (jobList.includes(JobIdTypes.AllJobs) || jobList.length > 1) {
                return JobIdTypes.AllJobs;
            }
            return jobList[0];
        }
    }

    if (isAllJobsSelected || selectedJobIds?.length > 1) {
        return JobIdTypes.AllJobs;
    } else if (selectedJobIds?.length === 1) {
        return selectedJobIds[0];
    }
    return JobIdTypes.NoJobs;
}

export function selectJobs(jobIds: number[], allJobsSelected: boolean) {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        selectedJobIds: jobIds,
        isAllJobsSelected: allJobsSelected,
    };
    setJobPickerState(storageValue);
}

export function setTemplateMode() {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        selectedJobIds: [],
        isAllJobsSelected: false,
        isTemplateMode: true,
    };
    setJobPickerState(storageValue);
}

export function getJobPickerState() {
    return (
        BTSessionStorage.get("bt-object-dangerousJobPickerState") ??
        BTLocalStorage.get("bt-object-dangerousJobPickerState")
    );
}

export function setJobPickerState(state: IJobPickerPersistantState) {
    BTSessionStorage.set("bt-object-dangerousJobPickerState", state);
    BTLocalStorage.set("bt-object-dangerousJobPickerState", state);
}

export function removeJobPickerState() {
    BTSessionStorage.remove("bt-object-dangerousJobPickerState");
    BTLocalStorage.remove("bt-object-dangerousJobPickerState");
}
